import React, { useRef, useState, useEffect, useCallback } from 'react';
import BonusBannerCard from '../BonusBannerCard/BonusBannerCard';

/**
 * Карусель бонусных баннеров с индикаторами и стрелками
 */
export default function BonusCarousel({ positions, expiresAt }) {
    const scrollRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [expired, setExpired] = useState(false);
    const total = positions.length;
    const handleExpired = useCallback(() => setExpired(true), []);

    // Отслеживаем скролл для обновления индикатора
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollLeft = container.scrollLeft;
            const itemWidth = container.offsetWidth;
            const index = Math.round(scrollLeft / itemWidth);
            setCurrentIndex(index);
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (index) => {
        const container = scrollRef.current;
        if (!container) return;
        const itemWidth = container.offsetWidth;
        container.scrollTo({ left: index * itemWidth, behavior: 'smooth' });
    };

    const canGoLeft = currentIndex > 0;
    const canGoRight = currentIndex < total - 1;

    if (total === 0 || expired) return null;

    return (
        <div className="mt-[10px] flex-shrink-0">
            {/* Индикаторы сверху если больше 1 баннера */}
            {total > 1 && (
                <div className="flex items-center justify-center gap-3 mb-2 px-[14px]">
                    {/* Стрелка влево */}
                    <button 
                        onClick={() => canGoLeft && scrollTo(currentIndex - 1)}
                        className={`w-6 h-6 flex items-center justify-center transition-opacity ${canGoLeft ? 'opacity-70' : 'opacity-20'}`}
                        disabled={!canGoLeft}
                    >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    {/* Точки */}
                    <div className="flex items-center gap-1.5">
                        {positions.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => scrollTo(i)}
                                className={`rounded-full transition-all ${
                                    i === currentIndex 
                                        ? 'w-6 h-2 bg-gradient-to-r from-[#DC1FFF] to-[#3A88F7]' 
                                        : 'w-2 h-2 bg-white/30'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Стрелка вправо */}
                    <button 
                        onClick={() => canGoRight && scrollTo(currentIndex + 1)}
                        className={`w-6 h-6 flex items-center justify-center transition-opacity ${canGoRight ? 'opacity-70' : 'opacity-20'}`}
                        disabled={!canGoRight}
                    >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Карусель */}
            <div 
                ref={scrollRef}
                className="overflow-x-auto snap-x snap-mandatory scrollbar-hide"
            >
                <div className="flex flex-row w-max">
                    {positions.map((position, i) => (
                        <div 
                            key={`bonus-${position.categoryId || i}`} 
                            className="w-screen px-[14px] snap-center flex-shrink-0"
                        >
                            <BonusBannerCard
                                position={position}
                                expiresAt={expiresAt}
                                onExpired={handleExpired}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
